// File: views/LandingPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/LandingPage.module.css';

import Home from './Landing/Home';
import Contact from './Landing/Contact';
import BottomNav from './Landing/BottomNav';

/* helper: fade-in any [data-anim] once it enters view */
function useScrollFade(scope) {
  useEffect(() => {
    if (!scope.current) return;
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.inView);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    scope.current
      .querySelectorAll('[data-anim]')
      .forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [scope]);
}

/* helper: track which section is currently in view */
function useScrollSpy(scope, setActive) {
  useEffect(() => {
    if (!scope.current) return;
    
    const sections = scope.current.querySelectorAll('section[id]');
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // For contact section, we want to be more lenient with the threshold
          const isContact = entry.target.id === 'contact';
          const threshold = isContact ? 0.2 : 0.5;
          
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            const id = entry.target.id;
            setActive(id);
          }
        });
      },
      { 
        threshold: [0.2, 0.5], // Multiple thresholds for better detection
        rootMargin: '-10% 0px -10% 0px' // Reduced margin for more precise detection
      }
    );
    
    sections.forEach(section => io.observe(section));
    return () => io.disconnect();
  }, [scope, setActive]);
}

/* Calendly loader */
function useCalendly(active) {
  useEffect(() => {
    if (active !== 'contact') return;
    const init = () => window.Calendly?.initInlineWidgets?.();
    if (document.getElementById('calendly-sdk')) {
      init();
      return;
    }
    const s = document.createElement('script');
    s.id = 'calendly-sdk';
    s.src = 'https://assets.calendly.com/assets/external/widget.js';
    s.async = true;
    s.onload = init;
    document.body.appendChild(s);
  }, [active]);
}

/* smooth scroll helper */
const smoothScroll = id =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

export default function LandingPage() {
  const ref = useRef(null);
  const [active, setActive] = useState('home');

  useScrollFade(ref);
  useScrollSpy(ref, setActive);
  useCalendly(active);

  const switchTo = id => {
    // fade out
    setActive('');
    // scroll
    smoothScroll(id);
    // fade in new
    setTimeout(() => setActive(id), 300);
  };

  return (
    <div ref={ref} className="landingPageRoot">
      <div className={styles.sec} data-active={active === 'home'}>
        <Home onNav={switchTo} />
      </div>


      <div  className={`${styles.sec} ${styles.contactpage}`} data-active={active === 'contact'}>
        <Contact />
      </div>

      <BottomNav active={active} onNav={switchTo} />
    </div>
  );
}
